import React, {
  FC,
  useMemo,
  useCallback,
  useEffect,
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  useRef,
} from 'react';
import {
  useFormContext,
} from 'react-hook-form';
import clsx from 'clsx';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import map from 'lodash/map';
import sum from 'lodash/sum';
import noop from 'lodash/noop';
import dayjs from 'utils/dayjs';
import {
  blobToDataUrl,
  isEmptyString,
} from '@populateui/utils';
import Dialog from 'pages/Dashboard/components/Dialog';
import {
  MultimediaType,
} from 'pages/Dashboard/utils/constants';
import {
  PatientCompact,
} from 'pages/Dashboard/pages/Encounters/types/patient';
import {
  useGetBillingMediaByCategories,
  useClaimSettings,
  useGetEOBByNumber,
  useGetEOBDataForBilling,
  useUploadBillingMedia,
} from 'pages/Dashboard/pages/Billing/hooks';
import AllocationStep, {
  AllocationStepTitle,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/AllocationStep';
import InitialEOBStep, {
  InitialEOBStepTitle,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/InitialEOBStep';
import SelectPatientStep from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/SelectPatientStep';
import ReviewStep, {
  ReviewStepTitle,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/ReviewStep';
import LookupStep, {
  LookupStepTitle,
} from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/LookupStep';
import {
  EOBServiceCodePaymentInfoDTO,
  EOBWithInsurancePaymentResponseDTO,
  MultiMediaResponse,
  EOBDataForClaimViewDTO,
  EOBClaimPaymentViewDTO,
  EOBExtractionResponseDTO,
} from 'dtos';
import {
  calculateEOBAllocated,
  AdjustmentExceptionMap,
  formatEOBData,
  formatParsedEOBData,
  getAdjustmentExceptionMap,
  formatPatientInfoList,
  getValidEOBServiceCodePaymentInfos,
} from 'pages/Dashboard/pages/Billing/Payments/utils';
import {
  formatDate,
} from 'utils/date';
import EOBPreviewButton from 'pages/Dashboard/pages/Billing/Payments/EOBDialog/EOBPreviewButton';
import {
  QueryObserverResult,
} from '@tanstack/react-query';
import {
  mediaEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  useNotify,
} from 'core/hooks';
import {
  DateRange,
} from 'types/dateRange';
import {
  FileLocalUrl,
} from 'pages/Dashboard/components/FilePicker';
import orderBy from 'lodash/orderBy';
import FileDragOverlayProvider from 'core/context/fileDragOverlay';
import {
  useGetAllowedFileTypes,
} from 'pages/Dashboard/hooks/media';
import {
  generateDropzoneAccept,
} from 'core/mimeMap';

function Title() {
  const { watch } = useFormContext();
  const [eobNumber, createdOn, eobId] = watch(['eob.eobNumber', 'eob.createdOn', 'eob.eobId']);

  const postedOn = useMemo(() => (
    isNil(eobId) || isNil(createdOn) ? '' : `(Created on ${formatDate(createdOn)})`
  ), [createdOn, eobId]);

  return (
    <div className="flex items-center gap-1">
      <h3 className="text-lg font-semibold">
        EOB
        {' # '}
        <span className="text-gray-700">{eobNumber}</span>
      </h3>
      <div className="text-sm font-semibold flex gap-1">
        {postedOn}
      </div>
    </div>
  );
}

const titles: readonly (FC | null)[] = Object.freeze([
  LookupStepTitle,
  InitialEOBStepTitle,
  null,
  AllocationStepTitle,
  ReviewStepTitle,
]);

const components: readonly FC[] = Object.freeze([
  LookupStep,
  InitialEOBStep,
  SelectPatientStep,
  AllocationStep,
  ReviewStep,
]);

export type WizardProps = {
  currentEOB?: EOBWithInsurancePaymentResponseDTO | null;
  parsedEOBData?: EOBWithInsurancePaymentResponseDTO;
  hasEOB: boolean;
  isReview: boolean;
  fetchingEOB: boolean;
  fetchingBillingData: boolean;
  fetchingMedia: boolean;
  hasDoc: boolean;
  visibleDoc: boolean;
  dateRange: DateRange;
  billingData: EOBDataForClaimViewDTO[];
  patients: PatientCompact[];
  currentPatient: PatientCompact | null;
  allocated: number;
  addPatient: (patient: PatientCompact | null) => void;
  removePatient: (patient: PatientCompact) => void;
  setHasEOB: Dispatch<SetStateAction<boolean>>;
  setInReview: Dispatch<SetStateAction<boolean>>;
  toggleDoc: Dispatch<SetStateAction<boolean>>;
  enableSearch: Dispatch<SetStateAction<boolean>>;
  setDateRange: Dispatch<SetStateAction<DateRange>>;
  onClose: () => void;
  hasEOBServiceCodePaymentInfos: boolean;
  eobFound: boolean;
  setEOBFound: Dispatch<SetStateAction<boolean>>;
  isManual?: boolean;
  fetchEobDoc?: () => Promise<QueryObserverResult<MultiMediaResponse[], Error>>;
  fetchBillingData?: () => Promise<QueryObserverResult<EOBDataForClaimViewDTO[], Error>>;
  handleEOBDrop: (urls: FileLocalUrl[], file?: File) => void;
  removeEOBDocument: () => void;
  eobId?: number;
  parsedEOBErrors?: Omit<EOBExtractionResponseDTO, 'eobWithInsurancePayment'>;
  isDiscrepancyDetailsOpen: boolean;
  setIsDiscrepancyDetailsOpen: Dispatch<SetStateAction<boolean>>;
  isExtracted: boolean;
  eobExtractionJobId?: number;
  adjustmentExceptionMap: AdjustmentExceptionMap;
};

export const WizardContext = createContext<WizardProps>({
  currentEOB: null,
  parsedEOBData: undefined,
  allocated: 0,
  currentPatient: null,
  billingData: [],
  dateRange: {
    min: new Date(),
    max: new Date(),
  },
  patients: [],
  hasEOB: false,
  fetchingBillingData: false,
  fetchingEOB: false,
  fetchingMedia: false,
  hasDoc: false,
  isReview: false,
  visibleDoc: false,
  eobFound: false,
  isManual: true,
  hasEOBServiceCodePaymentInfos: false,
  enableSearch: noop,
  onClose: noop,
  removePatient: noop,
  addPatient: noop,
  setDateRange: noop,
  setHasEOB: noop,
  setInReview: noop,
  toggleDoc: noop,
  setEOBFound: noop,
  handleEOBDrop: noop,
  removeEOBDocument: noop,
  eobId: 0,
  parsedEOBErrors: undefined,
  isDiscrepancyDetailsOpen: false,
  setIsDiscrepancyDetailsOpen: noop,
  isExtracted: false,
  adjustmentExceptionMap: {},
});

type Props = {
  onClose: () => void;
  defaultPatient?: PatientCompact;
  defaultStartDate?: string;
  parsedEOBData?: EOBWithInsurancePaymentResponseDTO;
  parsedEOBFile?: File;
  parsedEOBErrors?: Omit<EOBExtractionResponseDTO, 'eobWithInsurancePayment'>;
  eobExtractionJobId?: number;
};

export default function WizardProvider({
  onClose,
  defaultPatient,
  defaultStartDate: _defaultStartDate,
  parsedEOBData,
  parsedEOBFile,
  parsedEOBErrors,
  eobExtractionJobId,
}: Props) {
  const { setValue, watch, reset, getValues } = useFormContext();
  const {
    nonCoveredCarcSettings,
    autoWriteOffCarcSettings,
  } = useClaimSettings();
  const adjustmentExceptionMap = useMemo(
    () => getAdjustmentExceptionMap({
      nonCoveredCarcSettings,
      autoWriteOffCarcSettings,
    }),
    [nonCoveredCarcSettings, autoWriteOffCarcSettings],
  );
  const dialogRootRef = useRef<HTMLDivElement>(null!);
  const allowedFileTypes = useGetAllowedFileTypes(MultimediaType.EOB);
  const cleanExtensions = allowedFileTypes.split(',')
    .map((ext) => ext.trim().replace(/^\./, ''));
  const dropzoneAccept = generateDropzoneAccept(cleanExtensions);

  const now = dayjs().endOf('day');

  const defaultStartDate = useMemo(() => {
    const date = dayjs(_defaultStartDate);
    const sixMonthsAgo = now.subtract(6, 'months');

    return date.isValid() && date.isBefore(sixMonthsAgo) ? date.toDate() : null;
  }, [_defaultStartDate]);

  const [dateRange, setDateRange] = useState<DateRange>({
    min: defaultStartDate ?? now.subtract(6, 'months').toDate(),
    max: now.toDate(),
  });

  const [eobFound, setEOBFound] = useState<boolean>(false);
  const [patients, selectPatients] = useState<PatientCompact[]>([]);
  const [currentPatient, selectPatient] = useState<PatientCompact | null>(defaultPatient ?? null);
  const [isReview, setInReview] = useState<boolean>(false);
  const [hasEOB, setHasEOB] = useState<boolean>(false);
  const [searchEnabled, enableSearch] = useState<boolean>(false);
  const [docIsVisible, setDocIsVisible] = useState<boolean>(false);
  const [isDiscrepancyDetailsOpen, setIsDiscrepancyDetailsOpen] = useState<boolean>(false);
  const [eobId, eobNumber, eobDoc, paymentsInfo] = watch(['eob.eobId', 'eob.eobNumber', 'eobDoc', 'eob.eobServiceCodePaymentInfos']);
  const allocated = useMemo(() => calculateEOBAllocated(paymentsInfo), [paymentsInfo]);
  const hasEOBServiceCodePaymentInfos = getValidEOBServiceCodePaymentInfos(
    paymentsInfo,
  ).length > 0;

  const notify = useNotify();
  const { mutateAsync: uploadBillingMedia, isPending: isUploading } = useUploadBillingMedia();
  const {
    data,
    isFetching: fetchingEOB,
  } = useGetEOBByNumber(searchEnabled ? eobNumber : undefined);

  const wizardStep = useMemo(() => sum([
    Number(hasEOB),
    Number(eobFound),
    Number(!isNil(currentPatient) && !fetchingEOB),
    Number(isReview),
  ]), [hasEOB, eobFound, currentPatient, isReview, fetchingEOB]);

  const Component = components[wizardStep];
  const SectionTitle = titles[wizardStep];

  const {
    data: media = [],
    isFetching: fetchingMedia,
    refetch: fetchEobDoc,
  } = useGetBillingMediaByCategories(eobId ?? 0, [MultimediaType.EOB]);

  const {
    data: billingData = [],
    isFetching: fetchingBillingData,
    refetch: fetchBillingData,
  } = useGetEOBDataForBilling(currentPatient?.patientId ?? 0, dateRange);

  const isManual = /manual/i.test(data?.eob?.tag ?? 'Manual');
  const isExtracted = !isNil(parsedEOBData);

  useEffect(() => {
    if (!isEmptyString(eobNumber)) {
      enableSearch(true);
    }
  }, []);

  useEffect(() => {
    if (!isNil(parsedEOBData)) {
      const formattedEOBData = formatParsedEOBData(parsedEOBData, adjustmentExceptionMap);

      reset({
        ...formattedEOBData,
        insurancePayment: {
          ...formattedEOBData.insurancePayment,
          paymentDate: formattedEOBData.insurancePayment?.paymentDate
            ?? getValues('insurancePayment.paymentDate'),
        },
        ...(parsedEOBFile ? {
          eobDoc: {
            original: parsedEOBFile,
            media: {
              url: { value: URL.createObjectURL(parsedEOBFile) },
              fileName: parsedEOBFile.name,
            },
          },
        } : {}),
      }, { keepDirty: false });

      selectPatients(formatPatientInfoList(parsedEOBData?.patientInfoList ?? []));
      if (!isNil(parsedEOBFile)) {
        setDocIsVisible(true);
      }
      setHasEOB(true);
    }
  }, [adjustmentExceptionMap]);

  useEffect(() => {
    if (!fetchingEOB && searchEnabled) {
      if (!isEmpty(data)) {
        const formattedEOBData = formatEOBData(data, adjustmentExceptionMap);

        reset(formattedEOBData, { keepDirty: false });
        selectPatients(formatPatientInfoList(data?.patientInfoList ?? []));

        setEOBFound(true);
      }
      enableSearch(false);
      setHasEOB(true);
    }
  }, [adjustmentExceptionMap, data, fetchingEOB, searchEnabled, eobFound, reset]);

  useEffect(() => {
    if (media.length > 0 && !fetchingMedia) {
      setValue('eobDoc', {
        original: undefined,
        media: media[0],
      });
    }
  }, [media, fetchingMedia]);

  const handleClose = () => {
    if (!isNil(eobDoc?.media?.url?.value)) {
      URL.revokeObjectURL(eobDoc?.media?.url?.value ?? '');
    }
    onClose();
  };

  const hasDoc = !isNil(eobDoc) && wizardStep > 1 && wizardStep < 4;
  const visibleDoc = useMemo(() => hasDoc && docIsVisible, [hasDoc, docIsVisible]);

  useEffect(() => {
    if (hasDoc) {
      setDocIsVisible(true);
    }
  }, [hasDoc]);

  const selectedPatientIds = useMemo(() => (
    new Set(map(patients, 'patientId'))
  ), [patients]);

  const addPatient = useCallback((patient: PatientCompact | null) => {
    if (!isNil(patient) && !selectedPatientIds.has(patient?.patientId)) {
      selectPatients((currentPatients) => orderBy([
        ...currentPatients,
        patient,
      ], 'indexByLastName', 'asc'));
    }
    selectPatient(patient);
  }, [selectPatient, selectPatients, selectedPatientIds]);

  const removePatient = useCallback((patient: PatientCompact) => {
    if (selectedPatientIds.has(patient?.patientId)) {
      selectPatients((prevPatients) => {
        const index = prevPatients.findIndex(({ patientId }) => patientId === patient?.patientId);
        return [
          ...prevPatients.slice(0, index),
          ...prevPatients.slice(index + 1),
        ];
      });
      const servicePayments = getValues('eob.eobServiceCodePaymentInfos') ?? [];
      const billingClaimIdsForPatient = billingData
        .filter((item) => item?.claim?.patientId === patient?.patientId)
        .map((item) => item?.claim?.claimId ?? 0)
        .filter((claimId) => claimId > 0);

      const removedPatientClaimIds = new Set<number>(
        [
          ...billingClaimIdsForPatient,
          ...servicePayments
            .filter((payment: EOBServiceCodePaymentInfoDTO) => (
              payment?.claim?.patientId === patient?.patientId
            ))
            .map((payment: EOBServiceCodePaymentInfoDTO) => payment?.claim?.claimId ?? 0)
            .filter((claimId: number) => claimId > 0),
        ],
      );
      setValue('eob.eobServiceCodePaymentInfos', servicePayments.filter((payment: EOBServiceCodePaymentInfoDTO) => (
        payment?.claim?.patientId !== patient?.patientId
      )), { shouldDirty: true });

      const claimPayments = getValues('eob.eobClaimPayments') ?? [];
      setValue('eob.eobClaimPayments', claimPayments.filter((payment: EOBClaimPaymentViewDTO) => (
        payment?.claimPatientId !== patient?.patientId
        && !removedPatientClaimIds.has(payment?.claimId ?? 0)
      )), { shouldDirty: true });
    }
  }, [selectPatients, selectedPatientIds, billingData]);

  const uploadEOB = async () => {
    const dataUrl = await blobToDataUrl(
      eobDoc?.original ?? null,
    );
    try {
      await uploadBillingMedia({
        multimediaType: MultimediaType.EOB,
        fileName: eobDoc?.original?.name,
        dataUrl,
        originatorId: eobId,
      });
      fetchEobDoc?.();
      setDocIsVisible(true);
      notify({
        variant: 'success',
        message: 'Your EOB Document has been uploaded successfully.',
      });
    } catch (error) {
      if (!isNil(eobDoc?.media)) {
        fetchEobDoc?.();
      }
    }
  };

  const handleEOBDrop = (urls: FileLocalUrl[], file?: File) => {
    if (!isNil(file)) {
      setValue('eobDoc', {
        original: file,
        media: {
          url: { value: URL.createObjectURL(file) },
          fileName: file!.name,
        },
      });
      setDocIsVisible(true);
    }
  };

  useEffect(() => {
    if (eobDoc?.original && !isNil(eobId) && !isExtracted) {
      uploadEOB();
    }
  }, [eobDoc, isExtracted]);

  const removeEOBDocument = () => {
    URL.revokeObjectURL(eobDoc?.media?.url?.value!);
    setValue('eobDoc', null);
    setDocIsVisible(false);
  };

  useEffect(() => {
    const unsubscribe = mediaEventChannel.on('onMediaDeleted', () => {
      removeEOBDocument();

      fetchEobDoc?.();
    });
    return () => {
      unsubscribe();
    };
  }, []);

  const value = useMemo(() => ({
    currentEOB: data,
    parsedEOBData,
    allocated,
    addPatient,
    removePatient,
    fetchingEOB,
    fetchEobDoc,
    fetchBillingData,
    fetchingBillingData,
    fetchingMedia,
    enableSearch,
    hasEOB,
    billingData,
    patients,
    currentPatient,
    setInReview,
    setHasEOB,
    isReview,
    isManual,
    eobId,
    onClose: handleClose,
    toggleDoc: setDocIsVisible,
    docIsVisible,
    dateRange,
    setDateRange,
    visibleDoc,
    hasDoc,
    eobFound,
    setEOBFound,
    hasEOBServiceCodePaymentInfos,
    handleEOBDrop,
    removeEOBDocument,
    parsedEOBErrors,
    isDiscrepancyDetailsOpen,
    setIsDiscrepancyDetailsOpen,
    isExtracted,
    eobExtractionJobId,
    adjustmentExceptionMap,
  }), [
    eobId,
    data,
    handleClose,
    allocated,
    addPatient,
    removePatient,
    fetchingEOB,
    fetchingMedia,
    fetchBillingData,
    fetchingBillingData,
    billingData,
    patients,
    currentPatient,
    setInReview,
    setHasEOB,
    hasEOB,
    isReview,
    setDocIsVisible,
    docIsVisible,
    dateRange,
    setDateRange,
    visibleDoc,
    hasDoc,
    eobFound,
    setEOBFound,
    hasEOBServiceCodePaymentInfos,
    isManual,
    isExtracted,
    handleEOBDrop,
    removeEOBDocument,
    adjustmentExceptionMap,
    parsedEOBErrors,
    isDiscrepancyDetailsOpen,
    setIsDiscrepancyDetailsOpen,
    eobExtractionJobId,
  ]);

  return (
    <WizardContext.Provider value={value}>
      <Dialog
        dialogRef={dialogRootRef}
        open
        title={(
          <div className="flex w-full flex-1 justify-between item-center">
            <div className="flex flex-col">
              {wizardStep > 1 && <Title />}
              {!isNil(SectionTitle) && <SectionTitle />}
            </div>
            {wizardStep > 1 && wizardStep < 4 && <EOBPreviewButton />}
          </div>
        )}
        onClose={onClose}
        onConfirm={noop}
        disableEscapeKeyDown
        disableBackdropClick
        dialogClassName={(docIsVisible || wizardStep === 3 || wizardStep === 4) ? 'is-full' : ''}
        fullWidth={(wizardStep > 1 && docIsVisible) || wizardStep === 3 || wizardStep === 4}
        className={clsx('h-full overflow-y-hidden populate-form', {
          '!w-140': wizardStep < 2 && isNil(parsedEOBErrors),
          '!w-[40rem]': wizardStep < 2 && !isNil(parsedEOBErrors) && !isDiscrepancyDetailsOpen,
          '!w-[68rem]': wizardStep < 2 && isDiscrepancyDetailsOpen,
          '!w-full': (docIsVisible && wizardStep > 1) || wizardStep === 3 || wizardStep === 4,
          '!w-[72rem] !max-w-[calc(100vw-84px)]': !docIsVisible && wizardStep > 1,
          'transition-all duration-150 ease-in-out': wizardStep < 2 && !isNil(parsedEOBErrors),
        })}
        disableEnforceFocus
      >
        <FileDragOverlayProvider
          targetRef={dialogRootRef}
          onDropFile={(file) => { handleEOBDrop([], file as File); }}
          isUploading={Boolean(eobDoc?.original && !isNil(eobId) && isUploading)}
          disabled={!eobFound}
          messages={{ default: 'Drop your EOB file to upload', uploading: 'Uploading EOB...' }}
          backgroundClassName="bg-white -mt-5"
          dropzoneAccept={dropzoneAccept}
        >
          <Component />
        </FileDragOverlayProvider>
      </Dialog>
    </WizardContext.Provider>
  );
}
